/* MxL Showroom Appointment Request (Stylist Co. entrance page).
   - Request only: nothing is booked, reserved, or confirmed here.
   - Visual inline calendar; dates and slots are generated in the showroom's
     configured IANA timezone (section setting, verified against the Shopify
     store timezone), never the visitor's browser timezone. Calendar dates
     carry their weekday regardless of timezone, so slot windows are pure
     business-local wall times and DST needs no conversion.
   - Owner can block dates/slots via section settings (theme editor).
   - Submission: one Klaviyo client event (public company ID only) with a
     unique_id reference so retries can never double-send. No marketing
     subscription, no consent change, no credentials in this file.
   - Fully separate from the Stylist Pull access gate: submitting a request
     never grants portal access. */
(function () {
  var box = document.querySelector("[data-appt]");
  if (!box) return;
  var root = document.getElementById("MxlSp");
  var form = box.querySelector("[data-appt-form]");
  var dateInput = box.querySelector("[data-appt-date]");
  var calEl = box.querySelector("[data-appt-cal]");
  var calGrid = box.querySelector("[data-cal-grid]");
  var calTitle = box.querySelector("[data-cal-title]");
  var calPrev = box.querySelector("[data-cal-prev]");
  var calNext = box.querySelector("[data-cal-next]");
  var calAnnounce = box.querySelector("[data-cal-announce]");
  var timesWrap = box.querySelector("[data-appt-times-wrap]");
  var timesEl = box.querySelector("[data-appt-times]");
  var timesHeading = box.querySelector("[data-appt-times-heading]");
  var noTimes = box.querySelector("[data-appt-notimes]");
  var selectedLine = box.querySelector("[data-appt-selected]");
  var statusEl = box.querySelector("[data-appt-status]");
  var doneEl = box.querySelector("[data-appt-done]");

  /* Availability comes from the shared showroom schedule (shop metafield
     mxl_ops.appt_schedule, managed from the private Showroom Operations
     dashboard). The historical constants below are only a fallback for the
     brief window before the schedule document exists. */
  var SCHED = null;
  try { SCHED = JSON.parse((box.querySelector("[data-appt-schedule]") || {}).textContent || "null"); } catch (e) {}
  if (SCHED && !SCHED.weekly) SCHED = null;
  var TZ = (SCHED && SCHED.tz) || box.dataset.apptTz || "America/New_York";
  var WEEKS = SCHED ? Math.max(1, Math.min(26, SCHED.weeks_ahead || 8))
    : Math.max(2, Math.min(12, parseInt(box.dataset.apptWeeks || "8", 10)));
  var SLOT_MIN = (SCHED && SCHED.slot_min) || 45;
  var STEP_MIN = SLOT_MIN + ((SCHED && SCHED.buffer_min) || 0);
  var NOTICE_DAYS = SCHED && typeof SCHED.min_notice_days === "number" ? SCHED.min_notice_days : 1;
  /* weekday -> array of [open, close] windows (schedule supports several per day) */
  var WINDOWS = {};
  if (SCHED) {
    Object.keys(SCHED.weekly || {}).forEach(function (d) {
      if ((SCHED.weekly[d] || []).length) WINDOWS[d] = SCHED.weekly[d];
    });
  } else {
    WINDOWS = { 1: [["10:00", "18:00"]], 2: [["10:00", "18:00"]], 3: [["10:00", "16:00"]] };
  }
  var CUSTOM = (SCHED && SCHED.custom_hours) || {};
  var RANGES = (SCHED && SCHED.blocked_ranges) || {};
  var BOOKED = new Set((SCHED && SCHED.booked) || []);

  /* ---------- visitor timezone + canonical UTC (DST-safe) ----------
     The showroom schedule is authoritative in America/New_York (IANA id, so
     EST/EDT resolve automatically per date). Visitors see New York time
     first, with their own local equivalent alongside when detectable. */
  var VISITOR_TZ = "";
  try { VISITOR_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch (e) {}
  function tzOffsetAt(instant, tz) {
    var parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "longOffset" }).formatToParts(instant);
    var tzn = (parts.find(function (p) { return p.type === "timeZoneName"; }) || {}).value || "GMT+00:00";
    var m = tzn.match(/GMT([+-])(\d{2}):(\d{2})/);
    if (!m) return "+00:00";
    return m[1] + m[2] + ":" + m[3];
  }
  function nyToUtc(iso, hm) {
    /* two-pass: probe offset near the wall time, rebuild, re-check (correct
       across DST transitions; NY business hours never hit the 2 AM gap) */
    var off = tzOffsetAt(new Date(iso + "T" + hm + ":00Z"), TZ);
    var d = new Date(iso + "T" + hm + ":00" + off);
    var off2 = tzOffsetAt(d, TZ);
    if (off2 !== off) d = new Date(iso + "T" + hm + ":00" + off2);
    return d;
  }
  function localTimeLabel(iso, hm) {
    if (!VISITOR_TZ || VISITOR_TZ === TZ) return "";
    try {
      return nyToUtc(iso, hm).toLocaleTimeString("en-US",
        { hour: "numeric", minute: "2-digit", timeZone: VISITOR_TZ });
    } catch (e) { return ""; }
  }
  function dualLine(iso, slot) {
    var ny = slot.label + " New York time";
    var ls = localTimeLabel(iso, slot.start), le = localTimeLabel(iso, slot.end);
    return ls && le ? ny + " · " + ls + " to " + le + " your local time" : ny;
  }

  function parseList(s) {
    return new Set((s || "").split(/[\n,;]+/).map(function (x) { return x.trim(); }).filter(Boolean));
  }
  var blockedDates = parseList(box.dataset.apptBlockedDates);
  (SCHED && SCHED.blocked_dates || []).forEach(function (d) { blockedDates.add(d); });
  var blockedSlots = parseList(box.dataset.apptBlockedSlots);

  /* ---------- business-timezone date helpers ---------- */
  function todayInTz() {
    return new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(new Date());
  }
  function addDays(iso, n) {
    var p = iso.split("-");
    return new Date(Date.UTC(+p[0], p[1] - 1, +p[2] + n)).toISOString().slice(0, 10);
  }
  function weekdayOf(iso) {
    var p = iso.split("-");
    return new Date(Date.UTC(+p[0], p[1] - 1, +p[2])).getUTCDay();
  }
  function labelDate(iso) {
    var p = iso.split("-");
    return new Date(Date.UTC(+p[0], p[1] - 1, +p[2])).toLocaleDateString("en-US",
      { weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
  }
  /* "August 5, 2026 at 2:45 AM" in showroom (New York) time, DST included. */
  function nyStamp(d) {
    var p = {};
    new Intl.DateTimeFormat("en-US", {
      timeZone: TZ, month: "long", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true
    }).formatToParts(d || new Date()).forEach(function (x) { p[x.type] = x.value; });
    return p.month + " " + p.day + ", " + p.year + " at " +
           p.hour + ":" + p.minute + " " + (p.dayPeriod || "").toUpperCase();
  }
  function shortDate(iso) {
    var p = iso.split("-");
    return new Date(Date.UTC(+p[0], p[1] - 1, +p[2])).toLocaleDateString("en-US",
      { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" });
  }
  function minutes(hm) { var q = hm.split(":"); return +q[0] * 60 + +q[1]; }
  function hm24(m) { return String(Math.floor(m / 60)).padStart(2, "0") + ":" + String(m % 60).padStart(2, "0"); }
  function hmLabel(m) {
    var h = Math.floor(m / 60), mm = m % 60;
    return (((h + 11) % 12) + 1) + ":" + String(mm).padStart(2, "0") + " " + (h >= 12 ? "PM" : "AM");
  }

  /* ---------- slots (windows + custom hours + blocked ranges + booked) ---------- */
  function slotsFor(iso) {
    var wins = CUSTOM[iso] || WINDOWS[weekdayOf(iso)];
    if (!wins || !wins.length) return [];
    var ranges = RANGES[iso] || [];
    var out = [];
    wins.forEach(function (w) {
      for (var s = minutes(w[0]); s + SLOT_MIN <= minutes(w[1]); s += STEP_MIN) {
        var hm = hm24(s);
        if (blockedSlots.has(iso + " " + hm) || BOOKED.has(iso + " " + hm)) continue;
        var clash = ranges.some(function (r) { return minutes(r[0]) < s + SLOT_MIN && s < minutes(r[1]); });
        if (clash) continue;
        out.push({ start: hm, end: hm24(s + SLOT_MIN), label: hmLabel(s) + " to " + hmLabel(s + SLOT_MIN) });
      }
    });
    return out;
  }

  /* ---------- booking window: future Mon/Tue/Wed only, blocked removed ---------- */
  var validDates = {};
  (function buildDates() {
    var start = addDays(todayInTz(), Math.max(1, NOTICE_DAYS));
    for (var i = 0; i < WEEKS * 7; i++) {
      var d = addDays(start, i);
      if ((!WINDOWS[weekdayOf(d)] && !CUSTOM[d]) || blockedDates.has(d)) continue;
      var slots = slotsFor(d);
      if (!slots.length) continue;
      validDates[d] = slots;
    }
  })();

  /* ---------- visual calendar ---------- */
  var months = [];   /* ["2026-07", ...] only months containing valid dates */
  Object.keys(validDates).sort().forEach(function (d) {
    var m = d.slice(0, 7);
    if (months.indexOf(m) < 0) months.push(m);
  });
  var monthIdx = 0;  /* opens on the first month with availability */
  var selectedDate = "";
  var selectedSlot = "";

  function monthLabel(ym) {
    var p = ym.split("-");
    return new Date(Date.UTC(+p[0], p[1] - 1, 1)).toLocaleDateString("en-US",
      { month: "long", year: "numeric", timeZone: "UTC" });
  }
  function daysInMonth(ym) {
    var p = ym.split("-");
    return new Date(Date.UTC(+p[0], +p[1], 0)).getUTCDate();
  }
  function renderCal(announce) {
    var ym = months[monthIdx];
    calTitle.textContent = monthLabel(ym);
    calPrev.disabled = monthIdx === 0;
    calNext.disabled = monthIdx === months.length - 1;
    calGrid.innerHTML = "";
    ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].forEach(function (wd, k) {
      var h = document.createElement("span");
      h.className = "mxl-sp-cal-wd";
      h.textContent = wd;
      h.setAttribute("aria-label", ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][k]);
      calGrid.appendChild(h);
    });
    var firstWd = weekdayOf(ym + "-01");
    for (var b = 0; b < firstWd; b++) {
      var blank = document.createElement("span");
      blank.className = "mxl-sp-cal-blank";
      calGrid.appendChild(blank);
    }
    var total = daysInMonth(ym);
    for (var day = 1; day <= total; day++) {
      var iso = ym + "-" + String(day).padStart(2, "0");
      var btn = document.createElement("button");
      btn.type = "button";
      btn.className = "mxl-sp-cal-day";
      btn.textContent = day;
      btn.dataset.iso = iso;
      btn.tabIndex = -1;
      if (validDates[iso]) {
        btn.setAttribute("aria-label", labelDate(iso) + ", available");
        btn.setAttribute("aria-pressed", iso === selectedDate ? "true" : "false");
        if (iso === selectedDate) btn.classList.add("is-selected");
      } else {
        btn.disabled = true;
        btn.setAttribute("aria-label", labelDate(iso) + ", unavailable");
      }
      calGrid.appendChild(btn);
    }
    /* exactly one tab stop: the selected date if visible, else first available */
    var stop = selectedDate
      ? calGrid.querySelector('.mxl-sp-cal-day[data-iso="' + selectedDate + '"]:not([disabled])')
      : null;
    if (!stop) stop = calGrid.querySelector(".mxl-sp-cal-day:not([disabled])");
    if (stop) stop.tabIndex = 0;
    if (announce) calAnnounce.textContent = "Calendar showing " + monthLabel(ym);
  }
  calPrev.addEventListener("click", function () {
    if (monthIdx > 0) { monthIdx--; renderCal(true); }
  });
  calNext.addEventListener("click", function () {
    if (monthIdx < months.length - 1) { monthIdx++; renderCal(true); }
  });

  /* keyboard: arrows move between available dates, Enter/Space activates */
  calGrid.addEventListener("keydown", function (e) {
    var cur = e.target.closest(".mxl-sp-cal-day");
    if (!cur || cur.disabled) return;
    var enabled = Array.from(calGrid.querySelectorAll(".mxl-sp-cal-day:not([disabled])"));
    var i = enabled.indexOf(cur);
    var next = null;
    if (e.key === "ArrowRight") next = enabled[i + 1];
    else if (e.key === "ArrowLeft") next = enabled[i - 1];
    else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      var dir = e.key === "ArrowDown" ? 1 : -1;
      var target = addDays(cur.dataset.iso, dir * 7);
      next = calGrid.querySelector('.mxl-sp-cal-day[data-iso="' + target + '"]:not([disabled])');
      if (!next) next = enabled[i + dir];
    } else if (e.key === "Home") next = enabled[0];
    else if (e.key === "End") next = enabled[enabled.length - 1];
    else return;
    e.preventDefault();
    if (next) {
      enabled.forEach(function (b) { b.tabIndex = -1; });
      next.tabIndex = 0;
      next.focus();
    }
  });
  calGrid.addEventListener("click", function (e) {
    var btn = e.target.closest(".mxl-sp-cal-day");
    if (!btn || btn.disabled) return;
    selectDate(btn.dataset.iso);
  });

  /* ---------- date then time flow ---------- */
  function selectDate(iso) {
    if (!validDates[iso]) return;
    selectedDate = iso;
    selectedSlot = "";          /* changing the date always clears the time */
    dateInput.value = iso;
    renderCal(false);
    var slots = validDates[iso];
    timesWrap.hidden = false;
    timesHeading.textContent = "Available times for " + shortDate(iso);
    timesEl.innerHTML = "";
    noTimes.hidden = slots.length > 0;
    slots.forEach(function (s) {
      var lab = document.createElement("label");
      lab.className = "mxl-sp-time";
      var inp = document.createElement("input");
      inp.type = "radio";
      inp.name = "a_slot";
      inp.value = s.start;
      inp.className = "mxl-sp-vh";
      var span = document.createElement("span");
      span.textContent = s.label;
      var ll = localTimeLabel(iso, s.start);
      if (ll) lab.title = ll + " in your local time (" + VISITOR_TZ + ")";
      lab.appendChild(inp);
      lab.appendChild(span);
      timesEl.appendChild(lab);
    });
    setErrOn(calEl, "");
    setErrOn(timesEl, "");
    refreshSelected();
    calAnnounce.textContent = "Selected " + labelDate(iso) + ". " + slots.length + " appointment times available.";
  }
  timesEl.addEventListener("change", function (e) {
    if (e.target.name !== "a_slot") return;
    selectedSlot = e.target.value;
    timesEl.querySelectorAll(".mxl-sp-time").forEach(function (l) {
      l.classList.toggle("is-selected", l.querySelector("input").checked);
    });
    setErrOn(timesEl, "");
    refreshSelected();
  });
  function currentSlotObj() {
    if (!selectedDate || !selectedSlot) return null;
    return (validDates[selectedDate] || []).find(function (s) { return s.start === selectedSlot; }) || null;
  }
  function refreshSelected() {
    var s = currentSlotObj();
    selectedLine.textContent = s
      ? "Requested slot: " + labelDate(selectedDate) + " — " + dualLine(selectedDate, s)
      : "";
  }

  /* ---------- validation ---------- */
  function setErr(el, msg) {
    var field = el.closest(".mxl-sp-field");
    var err = field ? field.querySelector(".mxl-sp-err") : null;
    if (err) err.textContent = msg || "";
    el.setAttribute("aria-invalid", msg ? "true" : "false");
    return !msg;
  }
  function setErrOn(container, msg) {
    var field = container.closest(".mxl-sp-field");
    var err = field ? field.querySelector(".mxl-sp-err") : null;
    if (err) err.textContent = msg || "";
    container.setAttribute("aria-invalid", msg ? "true" : "false");
    return !msg;
  }
  function val(name) {
    var el = form.querySelector('[name="' + name + '"]');
    return el ? String(el.value).trim() : "";
  }
  function validate() {
    var ok = true;
    ok = setErr(form.querySelector('[name="a_name"]'), val("a_name") ? "" : "Please enter your name.") && ok;
    ok = setErr(form.querySelector('[name="a_email"]'), /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(val("a_email")) ? "" : "Please enter a valid email.") && ok;
    ok = setErr(form.querySelector('[name="a_phone"]'), val("a_phone").replace(/[^0-9]/g, "").length >= 7 ? "" : "Please enter a valid phone number.") && ok;
    var dateOk = !!validDates[dateInput.value] && dateInput.value === selectedDate;
    ok = setErrOn(calEl, dateOk ? "" : "Please choose an available date from the calendar.") && ok;
    var slotOk = dateOk && !!currentSlotObj() && !!timesEl.querySelector('input[name="a_slot"]:checked');
    ok = setErrOn(timesEl, slotOk ? "" : "Please choose an appointment time.") && ok;
    ok = setErr(form.querySelector('[name="a_purpose"]'), val("a_purpose") ? "" : "Please choose a purpose.") && ok;
    ok = setErr(form.querySelector('[name="a_socials"]'), val("a_socials").length <= 200 ? "" : "Please keep socials under 200 characters.") && ok;
    var mb = val("a_moodboard");
    ok = setErr(form.querySelector('[name="a_moodboard"]'), !mb || /^https?:\/\/\S+$/.test(mb) ? "" : "Links must start with http or https.") && ok;
    var agree = form.querySelector("[data-appt-agree]");
    ok = setErr(agree, agree.checked ? "" : "Please acknowledge that the appointment is pending confirmation.") && ok;
    return ok;
  }
  function focusFirstInvalid() {
    var bad = form.querySelector('[aria-invalid="true"]');
    if (!bad) return;
    if (bad === calEl) {
      var f = calGrid.querySelector('.mxl-sp-cal-day[tabindex="0"]') || calGrid.querySelector(".mxl-sp-cal-day:not([disabled])");
      if (f) f.focus();
    } else if (bad === timesEl) {
      var r = timesEl.querySelector("input");
      if (r) { r.focus(); return; }
      var f2 = calGrid.querySelector('.mxl-sp-cal-day[tabindex="0"]');
      if (f2) f2.focus();
    } else {
      bad.focus();
    }
  }

  /* ---------- submit: one Klaviyo client event, reference reused on retry ---------- */
  var reference = null;
  var submitting = false;
  form.addEventListener("submit", function (e) {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) {
      statusEl.textContent = "Please review the highlighted fields.";
      focusFirstInvalid();
      return;
    }
    submitting = true;
    var btn = form.querySelector("[data-appt-submit]");
    btn.disabled = true;
    statusEl.textContent = "Sending your request…";
    reference = reference || ("MXL-A-" + (function () { var A = "23456789BCDFGHJKMNPQRSTVWXYZ", s = ""; for (var i = 0; i < 4; i++) s += A.charAt(Math.floor(Math.random() * A.length)); return s; })());
    var slot = currentSlotObj();
    var props = {
      reference: reference,
      source: "Stylist Co. entrance page",
      access_path: "public entrance, no access code",
      submitted_at: new Date().toISOString(),
      requester_name: val("a_name"),
      email: val("a_email"),
      phone: val("a_phone"),
      preferred_date: selectedDate,
      preferred_date_label: labelDate(selectedDate),
      preferred_time: slot.label,
      preferred_start_24h: slot.start,
      preferred_end_24h: slot.end,
      duration_minutes: SLOT_MIN,
      schedule_version: SCHED ? (SCHED.updated || "seed") : "legacy",
      utc_start: nyToUtc(selectedDate, slot.start).toISOString(),
      utc_end: nyToUtc(selectedDate, slot.end).toISOString(),
      visitor_tz: VISITOR_TZ,
      visitor_time_label: (function () {
        var ls = localTimeLabel(selectedDate, slot.start), le = localTimeLabel(selectedDate, slot.end);
        return ls && le ? ls + " to " + le + " (" + VISITOR_TZ + ")" : "";
      })(),
      showroom_timezone: TZ,
      purpose: val("a_purpose"),
      secondary_contact: val("a_secondary"),
      talent: val("a_talent"),
      project: val("a_project"),
      project_type: val("a_project_type"),
      event_date: val("a_event_date"),
      categories: val("a_categories"),
      moodboard: val("a_moodboard"),
      guests: val("a_guests"),
      accessibility: val("a_accessibility"),
      notes: val("a_notes"),
      status_note: "Request only. Not confirmed until the Maui X Lolita team follows up. No products reserved. No portal access granted."
    };
    /* optional company and socials: include only when provided so the
       internal email omits them cleanly when blank */
    if (val("a_company")) props.company = val("a_company");
    if (val("a_socials")) props.socials = val("a_socials");
    var payload = {
      data: { type: "event", attributes: {
        unique_id: reference,
        metric: { data: { type: "metric", attributes: { name: "Showroom Appointment Request" } } },
        profile: { data: { type: "profile", attributes: { email: val("a_email") } } },
        properties: props
      } }
    };
    /* Customer receipt on its OWN metric ("Showroom Appointment Request
       Submitted") with canonical New York time plus the visitor's local
       equivalent. unique_id = reference + "-receipt" makes replays safe. */
    var COMPANY = root ? root.dataset.companyId : "RsF8DM";
    function apptReceiptPayload() {
      var ls = localTimeLabel(selectedDate, slot.start), le = localTimeLabel(selectedDate, slot.end);
      return { data: { type: "event", attributes: {
        unique_id: reference + "-receipt",
        metric: { data: { type: "metric", attributes: { name: "Showroom Appointment Request Submitted" } } },
        profile: { data: { type: "profile", attributes: { email: val("a_email") } } },
        properties: {
          portal_type: "appointment",
          appointment_reference: reference,
          requester_name: val("a_name"),
          requester_email: val("a_email"),
          phone: val("a_phone"),
          company: val("a_company"),
          purpose: val("a_purpose"),
          guests: val("a_guests"),
          showroom_date: selectedDate,
          showroom_date_label: labelDate(selectedDate),
          showroom_start_time: slot.start,
          showroom_end_time: slot.end,
          showroom_time_label: slot.label,
          showroom_timezone: TZ,
          requested_start_utc: nyToUtc(selectedDate, slot.start).toISOString(),
          requested_end_utc: nyToUtc(selectedDate, slot.end).toISOString(),
          visitor_timezone: VISITOR_TZ,
          visitor_local_start: ls,
          visitor_local_end: le,
          visitor_time_label: ls && le ? ls + " to " + le : "",
          notes: val("a_notes"),
          submitted_at: new Date().toISOString(),
          /* Preformatted for the confirmation email — see nyStamp(). Klaviyo
             skips the entire send with "Email Syntax Error" if the template
             uses a filter it rejects, so templates print values only. */
          submitted_at_label: nyStamp()
        } } } };
    }
    var apptReceiptQueued = false;
    fetch("https://a.klaviyo.com/client/events/?company_id=" + encodeURIComponent(COMPANY), {
      method: "POST",
      headers: { "Content-Type": "application/json", revision: "2025-04-15" },
      body: JSON.stringify(apptReceiptPayload())
    }).then(function (rr) { apptReceiptQueued = rr.ok; }).catch(function () { apptReceiptQueued = false; })
      .then(function () {
        props.receipt_queued = apptReceiptQueued;
        props.receipt_email = val("a_email");
        return fetch("https://a.klaviyo.com/client/events/?company_id=" + encodeURIComponent(COMPANY), {
          method: "POST",
          headers: { "Content-Type": "application/json", revision: "2025-04-15" },
          body: JSON.stringify(payload)
        });
      }).then(function (r) {
      if (!r.ok) throw new Error("HTTP " + r.status);
      form.hidden = true;
      doneEl.hidden = false;
      doneEl.querySelector("[data-appt-ref]").textContent = reference;
      doneEl.querySelector("[data-appt-summary]").textContent =
        "Requested slot: " + labelDate(selectedDate) + " — " + dualLine(selectedDate, slot) + ".";
      statusEl.textContent = "";
      reference = null;
      submitting = false;
      btn.disabled = false;
      var h = doneEl.querySelector("h3");
      if (h) { h.setAttribute("tabindex", "-1"); h.focus(); }
    }).catch(function () {
      submitting = false;
      btn.disabled = false;
      statusEl.textContent = "Something went wrong. Please check your connection and submit again.";
    });
  });

  /* ---------- hours copy generated from the live schedule ---------- */
  (function describeHours() {
    if (!SCHED) return;
    var hoursEl = box.querySelector("[data-appt-hours]");
    if (!hoursEl) return;
    var names = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    var parts = [];
    for (var d = 0; d < 7; d++) {
      var wins = WINDOWS[d];
      if (!wins || !wins.length) continue;
      parts.push(names[d] + " " + wins.map(function (w) {
        return hmLabel(minutes(w[0])) + "\u2013" + hmLabel(minutes(w[1]));
      }).join(" & "));
    }
    if (!parts.length) {
      hoursEl.textContent = "Showroom appointments are temporarily unavailable. Please check back soon.";
      return;
    }
    hoursEl.textContent = "Showroom appointments are available " + parts.join("; ") +
      ". All times are New York time (the Maui X Lolita showroom); daylight saving time is handled automatically.";
  })();

  /* ---------- boot ---------- */
  if (months.length) renderCal(false);
})();

/* v15 */
